import React from 'react';
import { Card, Row, Col, Button, Image } from 'react-bootstrap';
import { FaVideo, FaPhoneAlt, FaEye, FaRegHeart } from 'react-icons/fa';
import moment from 'moment';
import Swal from 'sweetalert2';
import defaultProfileImage from '../Pages/Images/defaultimage.webp';
import {
    getVisibleOfferBadgeText,
    getVisibleSourceLabel,
    hasHighlightedSourceLabel,
    hasVisibleOfferBadge,
    formatApplyForCountry,
} from '../../utils/candidateBadges';
import CandidateCardHeading from './CandidateCardHeading';

const safeParseArray = (field) => {
    if (Array.isArray(field)) return field;
    if (typeof field === 'string') {
        try {
            const parsed = JSON.parse(field.replace(/\\"/g, '"'));
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }
    return [];
};

const formatAvailableFrom = (startDate) => {
    if (!startDate) return 'Not specified';
    if (typeof startDate === 'string' && startDate.toLowerCase().includes('immediate')) {
        return 'Immediate';
    }
    try {
        const date = new Date(startDate);
        if (!isNaN(date.getTime())) {
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            });
        }
        return startDate;
    } catch (e) {
        return startDate;
    }
};

const getCandidateDisplayName = (candidate) => {
    const fullNameFromParts = [candidate.first_name, candidate.last_name]
        .filter(Boolean)
        .join(' ')
        .trim();
    const rawName = String(candidate.full_name || candidate.name || fullNameFromParts || '')
        .split('@')[0]
        .trim();
    if (!rawName) return 'Candidate';
    return rawName.charAt(0).toUpperCase() + rawName.slice(1);
};

const getJobPositionValue = (candidate) => {
    const jobPosition = safeParseArray(candidate.job_position);
    if (jobPosition.length > 0) {
        return jobPosition.join(', ').trim() || 'Not specified';
    }
    return candidate.job_position || 'Not specified';
};

const getLanguages = (candidate) => {
    const languages = safeParseArray(candidate.language);
    if (!languages.length) return 'Not specified';
    const names = languages.map((lang) => (typeof lang === 'string' ? lang : lang.language)).filter(Boolean);
    if (names.length >= 2) return `${names[0]}, ${names[1]}`;
    return names[0] || 'Not specified';
};

const formatSalary = (candidate) => {
    const usd = candidate.expected_monthly_salary_usd || candidate.expected_monthly_salary;
    if (usd) {
        const usdAmount = parseInt(usd, 10);
        if (!isNaN(usdAmount)) {
            const dhsAmount = Math.round(usdAmount * 3.67);
            return `${usdAmount} USD / ${dhsAmount} DHS`;
        }
    }
    return '0 USD / 0 DHS';
};

function AvailableCandidateCard({ candidate, onViewClick, onContactAction }) {
    if (!candidate) return null;

    const handleAuthAction = (actionType) => {
        if (typeof onContactAction === 'function') {
            onContactAction(actionType, candidate);
        }
    };

    const handleVideoCall = (e) => {
        e.stopPropagation();
        const user = JSON.parse(localStorage.getItem('user')) || {};
        if (!user || (user.role !== 'agency' && user.role !== 'agency_admin')) {
            Swal.fire('Access Denied', 'Video Call feature is only available to Agencies.', 'error');
        } else if (user.plan_name === 'Free') {
            Swal.fire({
                icon: 'error',
                title: 'Upgrade Required',
                text: 'Video Call feature is available for exclusive plans. Please upgrade.',
                confirmButtonText: 'Upgrade Now',
            }).then((result) => {
                if (result.isConfirmed) window.location.href = '/subscription';
            });
        } else {
            window.dispatchEvent(
                new CustomEvent('initiate-video-call', {
                    detail: {
                        candidateId: candidate?.user_id || candidate?.id,
                        candidateName: candidate?.first_name,
                    },
                })
            );
        }
    };

    const hasPurchasedBadge = Number(candidate.purchased_badge) === 1;
    const hasPurchasedTag = hasVisibleOfferBadge(candidate);
    const isNewCandidate =
        candidate.verification_status?.toLowerCase() !== 'verified' &&
        candidate.created_at &&
        moment().diff(moment(candidate.created_at), 'days') <= 7;

    return (
        <Card className="h-100 shadow-sm rounded card-body-home-allcandidate" style={{ marginTop: '4px' }}>
            <Card.Body className="d-flex flex-column justify-content-between">
                <CandidateCardHeading
                    candidate={candidate}
                    primaryValue={getJobPositionValue(candidate)}
                    primaryLabel=""
                    showGender
                />
                <div className="d-flex">
                    <div className="me-3 text-center" style={{ marginTop: '15px' }}>
                        <div className="candidate-label-container">
                            {hasPurchasedBadge && (
                                <div className="custom-label verified-label">
                                    <span className="verified-label-icon">&#10003;</span>
                                    <span>Verified</span>
                                </div>
                            )}
                            {isNewCandidate && (
                                <div className="custom-label" style={{ width: '50%' }}>
                                    New
                                </div>
                            )}
                        </div>

                        <div className="position-relative d-flex justify-content-center profile-img-container">
                            {hasPurchasedTag && (
                                <div className="allcandidates-offer-badge">
                                    {getVisibleOfferBadgeText(candidate)}
                                </div>
                            )}

                            {candidate.profile_photo ? (
                                <Image
                                    src={candidate.profile_photo}
                                    loading="lazy"
                                    roundedCircle
                                    className="border allcandidate-profile-photo"
                                    alt={`${candidate.first_name || 'Candidate'} profile`}
                                    style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                                />
                            ) : (
                                <div
                                    className="border allcandidate-profile-photo d-flex align-items-center justify-content-center bg-light rounded-circle overflow-hidden"
                                    style={{ width: '100px', height: '100px' }}
                                >
                                    <Image
                                        src={defaultProfileImage}
                                        roundedCircle
                                        className="w-100 h-100"
                                        alt="Default profile"
                                        style={{ objectFit: 'cover' }}
                                    />
                                </div>
                            )}
                        </div>

                        <div
                            className="very-active-label"
                            style={{
                                color: hasHighlightedSourceLabel(candidate) ? 'green' : '#111',
                                fontWeight: hasHighlightedSourceLabel(candidate) ? '500' : 'normal',
                            }}
                        >
                            {getVisibleSourceLabel(candidate)}
                        </div>
                    </div>

                    <div className="flex-grow-1 allcandidate-card-right">
                        <ul className="small ps-3 mb-0 domesticcustom-list domesticcandidate-normal-details allcandidatescustom-list employercandidate-details">
                            <li className="candidate-card-detail-name">
                                <strong>Name - </strong>
                                {getCandidateDisplayName(candidate)}
                            </li>
                            <li>
                                <strong>Job Position - </strong>
                                {getJobPositionValue(candidate)}
                            </li>
                            <li>
                                <strong>Salary - </strong>
                                {formatSalary(candidate)}
                            </li>
                            <li>
                                <strong>Experience -</strong>{' '}
                                {candidate.total_work_experience || 'Not specified'}
                            </li>
                            <li>
                                <strong>Language -</strong> {getLanguages(candidate)}
                            </li>
                            <li>
                                <strong>Apply for - </strong>{' '}
                                <span>{formatApplyForCountry(candidate.country_apply_for_job)}</span>
                            </li>
                            <li>
                                <strong>Country -</strong>{' '}
                                <span>{candidate.nationality || 'Not specified'}</span>
                            </li>
                            <li>
                                <strong>Current Location -</strong>{' '}
                                <span>{candidate.current_country || 'Not specified'}</span>
                            </li>
                            <li>
                                <strong>Accommodation - </strong>{' '}
                                <span>{candidate.accommodation_preference || 'Not specified'}</span>
                            </li>
                            <li>
                                <strong>Visa Status - </strong>{' '}
                                <span>{candidate.visa_type || 'Not specified'}</span>
                            </li>
                            <li>
                                <strong>Available From - </strong>{' '}
                                <span>{formatAvailableFrom(candidate.preferred_start_date)}</span>
                            </li>
                        </ul>
                    </div>
                </div>

                <Row className="mt-1">
                    <Col xs={3} className="px-1">
                        <Button
                            variant="light"
                            className="w-100 small-btn action-button"
                            onClick={handleVideoCall}
                        >
                            <FaVideo style={{ color: '#000' }} />
                        </Button>
                    </Col>
                    <Col xs={3} className="px-1">
                        <Button
                            variant="secondary"
                            style={{ backgroundColor: '#7177a6' }}
                            className="w-100 small-btn action-button gap-2"
                            onClick={() => handleAuthAction('call')}
                        >
                            <FaPhoneAlt /> Call
                        </Button>
                    </Col>
                    <Col xs={3} className="px-1">
                        <Button
                            variant="allcandidates-veiwbutton"
                            className="w-100 small-btn action-button gap-2 allcandidates-veiwbutton"
                            onClick={onViewClick}
                        >
                            <FaEye /> View
                        </Button>
                    </Col>
                    <Col xs={3} className="px-1">
                        <Button
                            variant="success"
                            className="w-100 small-btn action-button"
                            onClick={() => handleAuthAction('whatsapp')}
                        >
                            <i className="bi bi-whatsapp" />
                        </Button>
                    </Col>
                </Row>

                <Row className="mt-1 text-center text-muted small fw-medium border-top pt-2 last-buttons-row">
                    <Col
                        className="cursor-pointer no-underline d-flex align-items-center justify-content-center"
                        onClick={() => handleAuthAction('shortlist')}
                        style={{ whiteSpace: 'nowrap' }}
                    >
                        <FaRegHeart className="me-1" />
                        Like
                    </Col>
                    <Col
                        className="cursor-pointer no-underline d-flex align-items-center justify-content-center"
                        onClick={() => handleAuthAction('share')}
                        style={{ whiteSpace: 'nowrap' }}
                    >
                        <i className="bi bi-share me-1"></i> Share
                    </Col>
                    <Col
                        className="cursor-pointer no-underline d-flex align-items-center justify-content-center"
                        onClick={() => handleAuthAction('message')}
                        style={{ whiteSpace: 'nowrap' }}
                    >
                        <i className="bi bi-chat-dots me-1"></i> Message
                    </Col>
                    <Col
                        className="cursor-pointer no-underline d-flex align-items-center justify-content-center"
                        onClick={() => handleAuthAction('report')}
                        style={{ whiteSpace: 'nowrap' }}
                    >
                        <i className="bi bi-flag me-1"></i> Report
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
}

export default AvailableCandidateCard;
